import React, { useCallback, useEffect } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductRating,
  PodTapper,
  PodSpacer,
  PodRow,
  ProductBadge
} from '@thd-olt-component-react/product-pod';
import proptypes from 'prop-types';
const { bool: boolType, string: stringType, number: numType, shape, arrayOf } = proptypes;
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import {
  useDiscoveryZoneCarouselPodTelemetry,
  useNodeRef,
  useClickInteractionListener
} from '../hooks';

export const DiscoveryZonesPod = (props) => {
  const {
    // eslint-disable-next-line react/prop-types
    itemId,
    storeId,
    position,
    parent,
    scheme,
    anchorProduct,
    brandTitleMaxLine,
    zoneName,
    zonePosition,
    zoneSponsored,
    podResults,
    hideRatings,
    url,
    product
  } = props;

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();
  const [productPodRatingRef, productPodRatingNode] = useNodeRef();

  // -----------------
  // Telemetry
  // -----------------
  const {
    triggerClickOnImage,
    triggerClickOnHeader,
    triggerClickOnRating,
    triggerClickOnPod,
    triggerProductPodV7Click
  } = useDiscoveryZoneCarouselPodTelemetry({
    productPodNode: (!zoneSponsored) ? (null) : (productPodNode),
    itemId: product?.itemId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    position,
    slotId: product?.info?.sponsoredMetadata?.slotId
  });

  // Utility function to handle modifier (cmd/ctrl/shift) keys check
  const modifierClick = (event) => {
    return (event.metaKey || event.ctrlKey || event.shiftKey);
  };

  /**
   * Handler on: pod 'click'
   */
  const handleClick = useCallback((event) => {
    if (!zoneSponsored) return;
    if (modifierClick(event)) return;

    event.preventDefault();
    // --

    // Note: The following trigger call should be removed from this point once the
    //       product-pod tapper gets the same trigger implemented on it, otherwise
    //       the click will be triggered twice.

    // --
    triggerProductPodV7Click(); // analytic & ad-server

    // --
    triggerClickOnPod(); // new-relic
    window.location = url; // redirect

  }, [url, triggerClickOnPod, zoneSponsored]);

  /**
   * Handler on: image 'click'
   */
  const handleClickOnProductImage = useCallback((event) => {
    if (!zoneSponsored) return;
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnImage(); // new-relic
    window.location = url; // redirect
  }, [url, triggerClickOnImage, zoneSponsored]);

  /**
   * Handler on: header 'click'
   */
  const handleClickOnProductHeader = useCallback((event) => {
    if (!zoneSponsored) return;
    if (modifierClick(event)) return;
    event.preventDefault();
    // --
    triggerClickOnHeader(); // new-relic
    window.location = url; // redirect
  }, [url, triggerClickOnHeader, zoneSponsored]);

  /**
   * Handler on: rating 'click'
   */
  useEffect(() => {
    // check
    if (!productPodRatingNode) return undefined; // case: not mounted

    const selector = `a[href*="${product?.itemId}"]`;
    const element = productPodRatingNode.querySelector(selector);

    // check
    if (!element) return undefined;

    // --
    const handler = (event) => {
      if (!zoneSponsored) return;
      if (modifierClick(event)) return;
      event.preventDefault();
      // --
      triggerClickOnRating(); // new-relic
      window.location = url; // redirect
    };

    // install
    element.addEventListener('click', handler);
    // uninstall
    return () => {
      element.removeEventListener('click', handler);
    };
  }, [productPodRatingNode, url, product?.itemId, triggerClickOnRating, zoneSponsored]);
  // ---- ----

  // -----------------
  // New-Tab Tracker
  // -----------------

  useClickInteractionListener({
    targetNode: (!zoneSponsored) ? (null) : (productPodNode),
    itemId: product?.itemId,
    sponsoredId: product?.info?.sponsoredMetadata?.sponsoredId,
    trackSource: product?.info?.sponsoredMetadata?.trackSource,
    slotId: product?.info?.sponsoredMetadata?.slotId,
    position
  });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      data-testid="dz-product-pod"
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        analyticsData={{
          parent, position, scheme, anchorProduct, zoneName, zonePosition, podResults
        }}
        padding="sui-p-2"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-1"
              href={url}
              aria-label="Link"
              onClick={handleClick}
            />
            <PodSpacer minHeight="40px">
              <PodRow noPadding>
                <ProductBadge itemId={pod.itemId} storeId={storeId} />
              </PodRow>
            </PodSpacer>
            <PodTapper zIndex>
              <ProductImage
                itemId={pod.itemId}
                showSecondaryImage={pod.showSecondaryImage}
                onClick={handleClickOnProductImage}
              />
            </PodTapper>

            <PodTapper fitWidth zIndex>
              <ProductHeader
                brand="above"
                itemId={pod.itemId}
                brandTitleMaxLine={brandTitleMaxLine}
                disableShopThisCollection
                productNameLineHeight="tight"
                onClick={handleClickOnProductHeader}
              />
            </PodTapper>

            {!hideRatings
          && (
            <div ref={productPodRatingRef}>
              <PodTapper fitWidth zIndex>
                <ProductRating itemId={pod.itemId} />
              </PodTapper>
            </div>
          )}
            <PodTapper zIndex pointerNone aPointer buttonPointer spanPointer>
              <Price
                itemId={pod.itemId}
                large={false}
                storeId={storeId}
                displayEachUom={false}
                showPreferredPricingBadge
              />
            </PodTapper>
          </>
        )}
      />
    </div>
  );
};

DiscoveryZonesPod.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductIdentifier,
  ProductHeader,
  SuperSkuMiniSwatches,
  Price,
  ProductRating
);

DiscoveryZonesPod.propTypes = {
  storeId: stringType,
  position: numType,
  parent: stringType,
  scheme: stringType,
  anchorProduct: stringType,
  brandTitleMaxLine: numType,
  zoneName: stringType,
  zonePosition: stringType,
  zoneSponsored: boolType,
  podResults: numType,
  hideRatings: boolType,
  url: stringType.isRequired,
  product: shape({
    itemId: stringType.isRequired,
    identifiers: shape({
      canonicalUrl: stringType,
    }),
    info: shape({
      sponsoredBeacon: shape({
        onClickBeacons: arrayOf(stringType),
        onViewBeacons: arrayOf(stringType),
      }),
      sponsoredMetadata: shape({
        trackSource: stringType,
        campaignId: stringType,
        placementId: stringType,
        slotId: stringType,
        sponsoredId: stringType,
      }),
    }),
  }).isRequired,
};

DiscoveryZonesPod.defaultProps = {
  storeId: '',
  position: '',
  parent: '',
  scheme: 'drecs',
  anchorProduct: '',
  brandTitleMaxLine: 4,
  zoneName: '',
  zonePosition: '',
  zoneSponsored: false,
  podResults: 0,
  hideRatings: false
};
DiscoveryZonesPod.displayName = 'DiscoveryZonesPod';
